import { CLM } from "@/common/schema/claims";
import { ARE_EQUAL, dateTime, DateTime } from "@/fx/data-helpers";
import assert from "assert";
import { authOptionsSession } from "@/app/auth/auth-options-session";
import { getServerSession } from "next-auth/next"
import { Q } from "./queries";
import { CLP } from "@/common/schema/claims-process";

// STEPS TO PERFORM BEFORE THE END
// 1. Add [impersonated]By to HistoryRecord and created[Impersonated]By SysInfo

// STEPS TO PERFORM AT THE END
//   1. Transition CommandExecutionContext to use currentUser() and superUser() for userId, userRole, userName, userEmail, etc.
//   2. Remove userId, userRole, userName, userEmail, etc. from CommandExecutionContext
//   3. Update all usages of userId, userRole, userName, userEmail, etc. to use currentUser() and superUser() instead
//   4. Remove userId, userRole, userName, userEmail, etc. from context() and CTX.Service

export type LoggedInContext ={
  currentUser:      CLM.User
  superUser:        CLM.User
  isImpersonating?: boolean
  userTestingTags?: string[]
}

export type CommandExecutionContext = {
  hasSession?:      () => Promise<boolean>,
  userId?:          () => Promise<CLM.UserId>,
  loggedInContext?: () => Promise<LoggedInContext>,
  now?:             () => Promise<DateTime>,
}

const EMPTY_CONTEXT: CommandExecutionContext = {
  hasSession:      undefined,
  userId:          undefined,
  loggedInContext: undefined,
  now:             undefined,
};
export const CommandExecutionContext: CommandExecutionContext = {
  ...EMPTY_CONTEXT
}
export function overrideContext(overrides: Partial<CommandExecutionContext> | undefined) {
  if(overrides !== undefined) {
    Object.assign(CommandExecutionContext, overrides);
  }
}
export function resetContextOverrides() {
  overrideContext(EMPTY_CONTEXT);
}

export namespace CTX {
  export type Service = {
    userId:          () => Promise<CLM.UserId>,
    isLoggedIn:      () => Promise<boolean>,
    loggedInContext: () => Promise<LoggedInContext>,
    now:             () => Promise<DateTime>,
    isAuthorized:    ({ claimId }: { claimId: CLM.ClaimId }) => Promise<boolean>,
  }
}
export async function context({ Q }: { Q: Q.Service }): Promise<CTX.Service> {
  async function serverSession() {
    const session = await getServerSession(authOptionsSession);
    assert(session, "Session is not defined");
    return session;
  }
  async function isLoggedIn(): Promise<boolean> {
    const session = await getServerSession(authOptionsSession);
    return !!session;
  }
  async function userId(): Promise<CLM.UserId> {
    if (CommandExecutionContext.userId) {
      return CommandExecutionContext.userId();
    }

    const session = await serverSession();
    const rawUser = session.user.currentUser;
    const currentUser = CLM.fromRawUser()(rawUser);
    assert(currentUser, "Current user is not defined");
    return currentUser.id;
  }
  async function loggedInContext(): Promise<LoggedInContext> {
    if (CommandExecutionContext.loggedInContext) {
      return CommandExecutionContext.loggedInContext();
    }

    const session = await serverSession();
    assert(session.user,             "User is not defined");
    assert(session.user.currentUser, "Current user is not defined");
    const rawUser = session.user.currentUser;
    const rawSuperUser = session.user.superUser ?? session.user.currentUser;
    const currentUser = CLM.fromRawUser()(rawUser);
    const superUser   = CLM.fromRawUser()(rawSuperUser);
    return {
      currentUser,
      superUser,
      isImpersonating: !ARE_EQUAL.text(rawUser.id, rawSuperUser.id),
      userTestingTags: Array.from(new Set([
        ...(currentUser.testingTags?.map(_ => _.identifier) ?? []),
        ...(superUser.testingTags?.map(_ => _.identifier) ?? []),
      ])),
    };
  }
  function now(): Promise<DateTime> {
    if (CommandExecutionContext.now) {
      return CommandExecutionContext.now();
    }

    return Promise.resolve(
      dateTime(new Date())
    );
  }
  async function isAuthorized({ claimId }: { claimId: CLM.ClaimId }): Promise<boolean> {
    const { currentUser } = await loggedInContext();
    if (currentUser.settings.persona === CLP.ActorType.DISTRIBUTOR) {
      const distributorEmployee = CLM.distributorEmployeeReference_FROM_user({ user: currentUser });
      const [claim, distributors] = await Promise.all([
                                                Q.claim       ({ id:      claimId                }),
                                                Q.distributors({ filter: { distributorEmployee } })
                                              ]);

      return claim.distributor !== undefined && distributors.some((_) => (ARE_EQUAL.id(_.id, claim.distributor?.id)));
    }
    return true;
  }

  return { isLoggedIn, userId, loggedInContext, now, isAuthorized };
}
